"use client";

import Link from "next/link";
import type { RankedFestival } from "./scoring";
import { trackEvent } from "@/lib/analytics";

type Props = {
  ranked: RankedFestival;
  rank: number;
};

export function ResultCard({ ranked, rank }: Props) {
  const { festival, matchPercent, topAxis, hasLiveJobs } = ranked;

  const pitchline =
    festival.pitchlines[topAxis] ??
    Object.values(festival.pitchlines)[0] ??
    "";

  const meta = [festival.capacity?.toLocaleString(), festival.month]
    .filter(Boolean)
    .join(", ");

  function handleClick() {
    trackEvent("festival_quiz_result_clicked", {
      festival: festival.slug,
      rank,
      matchPercent,
    });
  }

  return (
    <div
      className="rounded-lg overflow-hidden shadow-md"
      style={{ background: festival.gradient }}
    >
      <div className="px-6 py-6 md:px-8 md:py-7 text-white">
        {/* Rank + match */}
        <div className="flex items-start justify-between gap-4 mb-3">
          <span
            className="text-[11px] font-semibold tracking-widest uppercase opacity-75"
            aria-label={`Match rank ${rank}`}
          >
            #{rank} match
          </span>
          <span className="text-[13px] font-semibold opacity-90 flex-shrink-0">
            {matchPercent}%
          </span>
        </div>

        {/* Name + emoji */}
        <h3 className="font-serif text-[24px] md:text-[28px] leading-[1.15] mb-2">
          {festival.emoji} {festival.name}
        </h3>

        {/* Location + meta */}
        <p className="text-[13px] opacity-80 mb-4">
          {festival.locationLabel}
          {meta && `, ${meta}`}
        </p>

        {/* Pitchline */}
        {pitchline && (
          <p className="text-[14px] md:text-[15px] leading-[1.55] opacity-90 mb-5">
            {pitchline}
          </p>
        )}

        {/* Jobs badge */}
        {hasLiveJobs && (
          <p className="text-[12px] font-semibold uppercase tracking-wide opacity-80 mb-4">
            Jobs available now
          </p>
        )}

        {/* CTA */}
        <Link
          href={`/festivals/${festival.slug}`}
          onClick={handleClick}
          className="inline-block bg-white/20 hover:bg-white/30 transition-colors text-white text-[13px] font-semibold px-4 py-2.5 rounded-sm"
        >
          Explore {festival.name} &rarr;
        </Link>
      </div>
    </div>
  );
}
